import React, {ReactNode} from 'react'

import OverlaySimple, {
  OverlaySimpleProps,
} from 'src/designSystem/components/organisms/Overlay/variants/OverlaySimple'
import PFText from 'src/designSystem/components/atoms/PFText/PFText'
import Box from 'src/designSystem/components/atoms/Box/Box'
import {SvgIcon} from 'src/designSystem/components/atoms/SvgIcon/SvgIcon'
import {
  SvgIconColorVariantNames as SvgIconColorVariant,
  SvgIconSize as SvgIconSizeVariant,
  SvgIconNames as SvgIconName,
} from 'src/designSystem/components/atoms/SvgIcon/SvgIcon.utils'
import {ButtonSize} from 'src/designSystem/components/atoms/Button/Button'

export type ErrorModalProps = {
  visible: boolean
  title?: string
  message?: string
  secondaryMessage?: string
  primaryButtonText?: string
  secondaryButtonText?: string
  onPrimaryAction?: () => void
  onSecondaryAction?: () => void
  primaryButtonSize?: ButtonSize
  secondaryButtonSize?: ButtonSize
  iconName?: SvgIconName
  iconColorVariant?: SvgIconColorVariant
  iconSizeVariant?: SvgIconSizeVariant
  isIconFilled?: boolean
  children?: ReactNode
  overlayProps?: Partial<OverlaySimpleProps>
  testID: string
}

/**
 * A component that displays an error modal with customizable content and actions.
 * It provides configurable title, messages, action buttons, and icons to handle
 * various error scenarios throughout the application.
 *
 * @param props - Component props
 * @param props.visible - Controls the visibility of the modal
 * @param props.title - Optional title text displayed at the top of the modal
 * @param props.message - Main error message displayed in the modal
 * @param props.secondaryMessage - Optional additional message displayed below the main message
 * @param props.primaryButtonText - Text for the primary action button
 * @param props.secondaryButtonText - Text for the secondary action button
 * @param props.handleOnPrimaryAction - Function called when the primary button is pressed
 * @param props.handleOnSecondaryAction - Function called when the secondary button is pressed
 * @param props.primaryButtonSize - Size variant for the primary button
 * @param props.secondaryButtonSize - Size variant for the secondary button
 * @param props.iconName - Name of the icon to display
 * @param props.iconColorVariant - Color variant for the icon
 * @param props.iconSizeVariant - Size variant for the icon
 * @param props.isIconFilled - Whether the icon should be filled or outlined
 * @param props.children - Optional custom content to replace default message display
 * @param props.overlayProps - Additional props to pass to the underlying OverlaySimple component
 * @param props.testID - Test ID for component testing
 */
export const ErrorModal = ({
  visible,
  title,
  message,
  secondaryMessage,
  primaryButtonText,
  secondaryButtonText,
  onPrimaryAction: handleOnPrimaryAction,
  onSecondaryAction: handleOnSecondaryAction,
  primaryButtonSize,
  secondaryButtonSize,
  iconName,
  iconColorVariant,
  iconSizeVariant,
  isIconFilled,
  children,
  overlayProps,
  testID,
}: ErrorModalProps): JSX.Element => {
  const icon = iconName ? (
    <SvgIcon
      name={iconName}
      colorVariant={iconColorVariant ?? 'default'}
      size={iconSizeVariant}
      isFilled={isIconFilled}
      testID={`${testID}-${iconName}-Icon`}
    />
  ) : null

  return (
    <OverlaySimple
      visible={visible}
      title={title}
      titleAlign="center"
      image={icon}
      okayButtonText={primaryButtonText}
      okayButtonSize={primaryButtonSize}
      onOkay={handleOnPrimaryAction}
      dismissButtonText={secondaryButtonText}
      dismissButtonSize={secondaryButtonSize}
      onDismiss={handleOnSecondaryAction}
      testID={testID}
      {...overlayProps}
    >
      {children || (
        <Box gap="medium" align="center">
          {message ? (
            <PFText variant="p" textAlign="center">
              {message}
            </PFText>
          ) : null}
          {secondaryMessage ? (
            <PFText variant="p" textAlign="center">
              {secondaryMessage}
            </PFText>
          ) : null}
        </Box>
      )}
    </OverlaySimple>
  )
}
